/**
 * English: Filter bar + responsive card grid for device agents.
 * Thai: แถบกรอง + grid การ์ด device agents
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../shared/LoadingSpinner';
import AgentManagementCard from './AgentManagementCard';
import { useAgentManagementFilters } from './useAgentManagementFilters';
import { useAgentManagementOrgContext } from './useAgentManagementOrgContext';
import type { AgentRow } from './useAgentRows';
import { useMemorySummaryDaily } from '../../hooks/useMemorySummary';

interface AgentManagementGridProps {
  rows: AgentRow[];
  loading: boolean;
  awaitingStation: boolean;
  deviceTypes: string[];
  orgName?: string;
  orgId?: string;
  stationId?: string;
}

export default function AgentManagementGrid({
  rows,
  loading,
  awaitingStation,
  deviceTypes,
  orgName = '',
  orgId: orgIdProp,
  stationId: stationIdProp,
}: AgentManagementGridProps) {
  const { t } = useTranslation();
  const { orgId, stationId } = useAgentManagementOrgContext({
    orgId: orgIdProp,
    stationId: stationIdProp,
  });
  const stationDaily = useMemorySummaryDaily(orgId, stationId, 30, 0, 45);
  const dailyItems = useMemo(
    () => stationDaily.data?.items ?? [],
    [stationDaily.data],
  );
  const { search, setSearch, typeFilter, setTypeFilter, filtered } = useAgentManagementFilters(rows);

  const showSpinner = (loading || awaitingStation) && rows.length === 0;

  if (showSpinner) {
    return (
      <div className="flex min-h-[240px] items-center justify-center rounded-2xl border border-app-border bg-app-surface">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  if (!loading && !awaitingStation && rows.length === 0) {
    return (
      <section className="rounded-2xl border border-app-border bg-app-surface p-10 text-center">
        <p className="mx-auto max-w-lg text-sm text-app-text-dim">{t('agents.empty')}</p>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-app-border/80 bg-app-surface shadow-sm overflow-hidden">
      <div className="flex flex-col gap-3 border-b border-app-border/60 bg-app-muted/15 px-4 py-4 sm:flex-row sm:items-end sm:justify-between sm:px-5">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-title-sm font-semibold text-ink">{t('agents.deviceAgentsTitle')}</h2>
            <span className="rounded-full bg-app-accent/15 px-2.5 py-0.5 text-[11px] font-semibold tabular-nums text-app-accent">
              {filtered.length}
              <span className="font-normal text-app-text-dim"> / {rows.length}</span>
            </span>
          </div>
          <p className="mt-1 text-sm text-app-text-sec">{t('agents.deviceAgentsDescription')}</p>
        </div>

        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
          <div className="relative min-w-0 flex-1 sm:w-56 md:w-64">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-app-text-dim" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('agents.searchPlaceholder')}
              className="w-full rounded-lg border border-app-border bg-app-bg py-2 pl-9 pr-3 text-sm text-app-text placeholder:text-app-text-dim focus:outline-none focus:ring-2 focus:ring-app-accent/30"
            />
          </div>
          <select
            id="agent-mgmt-type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            aria-label={t('agents.filterType')}
            className="rounded-lg border border-app-border bg-app-bg px-3 py-2 text-sm text-app-text sm:min-w-[140px] focus:outline-none focus:ring-2 focus:ring-app-accent/30"
          >
            <option value="">{t('agents.filterAllTypes')}</option>
            {deviceTypes.map((typeName) => (
              <option key={typeName} value={typeName}>
                {typeName}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="p-4 sm:p-5">
        {filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed border-app-border bg-app-muted/10 py-12 text-center">
            <p className="text-sm text-app-text-dim">{t('agents.filterNoMatch')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {filtered.map((row) => (
              <AgentManagementCard
                key={row.agent.id}
                row={row}
                orgName={orgName}
                stationId={stationId}
                stationDailyItems={dailyItems}
                memoryLoading={stationDaily.loading}
              />
            ))}
          </div>
        )}

        {(loading || awaitingStation) && rows.length > 0 ? (
          <p className="mt-4 flex items-center gap-2 text-xs text-app-text-dim">
            <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-app-accent border-t-transparent" />
            {t('agents.refreshing')}
          </p>
        ) : null}
      </div>
    </section>
  );
}
